import React, { useState, useMemo } from 'react';
import { Search, Trophy, Calendar, MapPin } from 'lucide-react';
import { winnersData } from '../mockData';
import { Input } from '../components/ui/input';

const Awards = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');

  // Get unique years
  const years = useMemo(() => {
    const uniqueYears = [...new Set(winnersData.map(w => w.year))];
    return uniqueYears.sort((a, b) => b - a);
  }, []);

  // Filter winners based on search and year
  const filteredWinners = useMemo(() => {
    // Don't show any winners if there's no search query
    if (!searchQuery.trim()) {
      return [];
    }
    
    return winnersData.filter(winner => {
      const matchesSearch = 
        winner.winner.toLowerCase().includes(searchQuery.toLowerCase()) ||
        winner.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        winner.country.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesYear = selectedYear === 'all' || winner.year.toString() === selectedYear;
      
      return matchesSearch && matchesYear;
    });
  }, [searchQuery, selectedYear]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-cyan-500 font-semibold text-sm uppercase tracking-wide">Excellence Recognition</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-2 mb-6">Award Winners</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Celebrating outstanding performance and ethical excellence in the forex industry
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by broker, category, or country..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-gray-50 border-gray-200 focus:border-cyan-500 focus:ring-cyan-500 rounded-xl"
              />
            </div>

            {/* Year Filter */}
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full pl-12 h-12 bg-gray-50 border border-gray-200 rounded-xl focus:border-cyan-500 focus:ring-cyan-500 focus:outline-none text-gray-700"
              >
                <option value="all">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            {searchQuery ? (
              <>Showing <span className="font-semibold text-gray-900">{filteredWinners.length}</span> result{filteredWinners.length !== 1 ? 's' : ''}</>
            ) : (
              <span className="text-gray-500">Enter a search term to find award winners</span>
            )}
          </div>
        </div>

        {/* Winners Grid */}
        {!searchQuery ? (
          <div className="bg-white rounded-2xl p-12 shadow-lg text-center">
            <div className="w-20 h-20 bg-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Search className="text-cyan-500" size={40} />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">Search for Award Winners</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Use the search box above to find award-winning brokers by name, category, or country
            </p>
          </div>
        ) : filteredWinners.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWinners.map((winner) => (
              <div
                key={winner.id}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Trophy className="text-white" size={28} />
                  </div>
                  <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-lg text-sm font-semibold">
                    {winner.year}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {winner.winner}
                </h3>

                <div className="flex items-center space-x-2 text-gray-600 text-sm mb-3">
                  <MapPin size={16} className="text-cyan-500" />
                  <span>{winner.country}</span>
                </div>

                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                    {winner.category}
                  </span>
                </div>

                <p className="text-gray-600 text-sm leading-relaxed">
                  {winner.description}
                </p>

                {winner.website && (
                  <a
                    href={winner.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                  >
                    Visit Website →
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-lg text-center">
            <Trophy className="mx-auto text-gray-300 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h3>
            <p className="text-gray-600">Try adjusting your search criteria or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Awards;